import { useCallback, useEffect, useState } from "react";
import { apiService, apiClient, graphBuilderClient } from "../../../services/api";
import { invalidateNodeCache } from "../nodeCache";
import "../synapses/Synapses.css";
import NodeForm from "../node/NodeForm";

const KIND_NODE = "node";

const createRateCodingEntry = (overrides = {}) => ({
  tempId: `rc-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  id: null,
  version: "",
  isActive: "true",
  structuredEncodingJson: "",
  naturalLanguageEncoding: "",
  fromTemplateId: "",
  notes: "",
  ...overrides,
});

const mapRateCodingFromRelated = (entries = []) =>
  (entries || []).map((entry, index) =>
    createRateCodingEntry({
      tempId: entry.id || `existing-rc-${index}`,
      id: entry.id || null,
      version:
        entry.version === 0 || entry.version
          ? String(entry.version)
          : "",
      isActive: entry.isActive === false ? "false" : "true",
      structuredEncodingJson: entry.structuredEncoding
        ? JSON.stringify(entry.structuredEncoding, null, 2)
        : "",
      naturalLanguageEncoding: entry.naturalLanguageEncoding || "",
      fromTemplateId: entry.fromTemplateId || "",
      notes: entry.notes || "",
    })
  );

const createNodeSettingEntry = (overrides = {}) => ({
  tempId: `ns-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  id: null,
  neuronTemplateId: "",
  nodeSettingType: "",
  nodeSettingsName: "",
  explanation: "",
  description: "",
  boundedDataJson: "",
  nodeNeuronSettingsJson: "",
  dataClassificationId: "",
  ...overrides,
});

const stringifyJsonField = (value) => {
  if (value === null || value === undefined) {
    return "";
  }
  if (typeof value === "string") {
    if (!value.trim()) {
      return "";
    }
    try {
      const parsed = JSON.parse(value);
      return JSON.stringify(parsed, null, 2);
    } catch {
      return value;
    }
  }
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return "";
  }
};

const mapNodeSettingsFromRelated = (entries = []) =>
  (entries || []).map((entry, index) =>
    createNodeSettingEntry({
      tempId: entry.id || `existing-ns-${index}`,
      id: entry.id || null,
      neuronTemplateId: entry.neuronTemplateId || "",
      nodeSettingType: entry.type || "",
      nodeSettingsName: entry.name || "",
      explanation: entry.explanation || "",
      description: entry.description || "",
      boundedDataJson: stringifyJsonField(entry.boundedData),
      nodeNeuronSettingsJson: stringifyJsonField(entry.nodeNeuronSettings),
      dataClassificationId: entry.dataClassificationId || "",
    })
  );

const createRemediationEntry = (overrides = {}) => ({
  tempId: `rem-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  id: null,
  reason: "",
  autoAttempted: "false",
  autoAttemptNotes: "",
  status: "OPEN",
  resolutionNotes: "",
  detectedAt: "",
  lastUpdated: "",
  ...overrides,
});

const mapRemediationFromRelated = (entries = []) =>
  (entries || []).map((entry, index) =>
    createRemediationEntry({
      tempId: entry.id || `existing-rem-${index}`,
      id: entry.id || null,
      reason: entry.reason || "",
      autoAttempted: entry.autoAttempted === false ? "false" : "true",
      autoAttemptNotes: entry.autoAttemptNotes || "",
      status: entry.status || "OPEN",
      resolutionNotes: entry.resolutionNotes || "",
      detectedAt: entry.detectedAt || "",
      lastUpdated: entry.lastUpdated || "",
    })
  );

const createNodeState = () => ({
  nodeId: null,
  loading: false,
  saving: false,
  error: null,
  success: null,
  form: {
    name: "",
    type: "",
    description: "",
    aliases: [],
    attributesJson: "",
    fusionMode: "",
    confidenceTiebreak: "",
    fusionParameters: "",
    retentionPolicyId: "",
    nodeKindId: "",
  },
  rateCoding: [],
  settings: [],
  remediation: [],
  effectiveSettings: {
    resolvedSettingsJson: "",
    resolvedAt: "",
    source: "",
  },
});

const NodeEntityModal = ({
  isOpen,
  node,
  onClose,
  onEntityUpdated,
  graphId,
  mlcfMetadata,
}) => {
  const [nodeState, setNodeState] = useState(createNodeState());

  const nodeId = node?.id || node?.nodeId;

  const mlcfFromNode = nodeState.node?.mlcf || null;
  const effectiveMlcf = mlcfMetadata || mlcfFromNode;

  useEffect(() => {
    if (!isOpen) {
      setNodeState(createNodeState());
    } else if (isOpen && !node) {
      // In add mode, initialize form with default values
      setNodeState(createNodeState());
    }
  }, [isOpen, node]);

  // Helper functions for attributes (JSON format)
  const attributesToJson = (attributes) => {
    if (!attributes) {
      return JSON.stringify({ attributes: [] }, null, 2);
    }
    if (Array.isArray(attributes)) {
      return JSON.stringify({ attributes }, null, 2);
    }
    if (typeof attributes === "object") {
      return JSON.stringify(attributes, null, 2);
    }
    return JSON.stringify({ attributes: [attributes] }, null, 2);
  };

  const parseAttributesFromJson = (jsonText) => {
    if (!jsonText || !jsonText.trim()) {
      return null;
    }
    try {
      const parsed = JSON.parse(jsonText);
      // Backend expects either an array or an object with 'attributes' key
      if (Array.isArray(parsed)) {
        return parsed;
      } else if (parsed.attributes && Array.isArray(parsed.attributes)) {
        return parsed.attributes;
      } else {
        // If it's an object, keep it as is for full JSON support
        return parsed;
      }
    } catch (error) {
      throw new Error(`Invalid JSON in attributes: ${error.message}`);
    }
  };

  const loadNodeDetails = useCallback(
    async (nodeIdToLoad) => {
      if (!nodeIdToLoad || !isOpen) {
        return;
      }
      setNodeState((prev) => ({
        ...createNodeState(),
        ...prev,
        nodeId: nodeIdToLoad,
        loading: true,
        error: null,
        success: null,
      }));
      try {
        const response = await apiService.getNode(nodeIdToLoad, {
          include_related: true,
        });
        if (response.success && response.node) {
          const loadedNode = response.node;
          const related = loadedNode.related || {};
          const fusionPolicy = related.fusionPolicy || {};
          const effectiveSettings = related.effectiveSettings || {};
          setNodeState((prev) => ({
            ...prev,
            node: loadedNode,
            nodeId: nodeIdToLoad,
            loading: false,
            form: {
              name: loadedNode.name || "",
              type: loadedNode.type || "",
              description: loadedNode.description || "",
              aliases: Array.isArray(loadedNode.aliases) ? loadedNode.aliases : [],
              attributesJson: attributesToJson(loadedNode.attributes),
              fusionMode: fusionPolicy.fusionMode || "",
              confidenceTiebreak: fusionPolicy.confidenceTiebreak || "",
              fusionParameters: fusionPolicy.parameters
                ? JSON.stringify(fusionPolicy.parameters, null, 2)
                : "",
              retentionPolicyId: loadedNode.retentionPolicyId || "",
              nodeKindId: loadedNode.nodeKindId || "",
            },
            rateCoding: mapRateCodingFromRelated(related.rateCoding),
            settings: mapNodeSettingsFromRelated(related.settings),
            remediation: mapRemediationFromRelated(related.remediation),
            effectiveSettings: {
              resolvedSettingsJson: effectiveSettings.resolvedSettings
                ? JSON.stringify(effectiveSettings.resolvedSettings, null, 2)
                : "",
              resolvedAt: effectiveSettings.resolvedAt || "",
              source: effectiveSettings.source || "",
            },
          }));
        } else {
          throw new Error(response.message || "Unable to load node details.");
        }
      } catch (error) {
        setNodeState((prev) => ({
          ...prev,
          loading: false,
          error: error.message || "Failed to load node.",
        }));
      }
    },
    [isOpen]
  );

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    // Only load details if we have a node (not in add mode)
    if (node && nodeId) {
      loadNodeDetails(nodeId);
    }
  }, [isOpen, loadNodeDetails, node, nodeId]);

  const handleNodeInputChange = (kind, event) => {
    const { name, value } = event.target;
    setNodeState((prev) => ({
      ...prev,
      form: {
        ...prev.form,
        [name]: value,
      },
    }));
  };

  const handleCreateNode = async () => {
    if (!graphId) {
      setNodeState((prev) => ({
        ...prev,
        error: "Graph ID is required to create a node.",
      }));
      return;
    }

    setNodeState((prev) => ({
      ...prev,
      saving: true,
      error: null,
      success: null,
    }));

    // Parse attributes JSON
    let attributes = null;
    try {
      attributes = parseAttributesFromJson(nodeState.form.attributesJson);
    } catch (error) {
      setNodeState((prev) => ({
        ...prev,
        saving: false,
        error: error.message || "Invalid JSON in attributes.",
      }));
      return;
    }

    const payload = {
      graphId,
      name: nodeState.form.name.trim(),
      type: nodeState.form.type.trim(),
      description: nodeState.form.description.trim(),
      aliases: Array.isArray(nodeState.form.aliases) ? nodeState.form.aliases : [],
    };

    // Only include attributes if they were provided
    if (attributes !== null) {
      payload.attributes = attributes;
    }

    // Add retentionPolicyId if provided
    if (
      nodeState.form.retentionPolicyId !== undefined &&
      nodeState.form.retentionPolicyId !== ""
    ) {
      payload.retentionPolicyId =
        nodeState.form.retentionPolicyId.trim() || null;
    }

    // Add nodeKindId if provided
    if (
      nodeState.form.nodeKindId !== undefined &&
      nodeState.form.nodeKindId !== ""
    ) {
      payload.nodeKindId = nodeState.form.nodeKindId.trim() || null;
    }

    // Add related entities if they have values
    const related = {};
    if (
      nodeState.form.fusionMode ||
      nodeState.form.confidenceTiebreak ||
      nodeState.form.fusionParameters
    ) {
      related.fusionPolicy = {
        fusionMode: nodeState.form.fusionMode.trim() || undefined,
        confidenceTiebreak:
          nodeState.form.confidenceTiebreak.trim() || undefined,
        parameters: nodeState.form.fusionParameters
          ? (() => {
              try {
                return JSON.parse(nodeState.form.fusionParameters);
              } catch {
                return {};
              }
            })()
          : undefined,
      };
    }

    // Handle rate coding entries (many-to-one)
    const rateCodingEntries = nodeState.rateCoding || [];
    const rateCodingPayload = [];
    for (let index = 0; index < rateCodingEntries.length; index += 1) {
      const entry = rateCodingEntries[index];
      const versionValue = entry.version?.toString().trim();
      const hasContent =
        (versionValue && versionValue.length > 0) ||
        (entry.structuredEncodingJson &&
          entry.structuredEncodingJson.trim().length > 0) ||
        (entry.naturalLanguageEncoding &&
          entry.naturalLanguageEncoding.trim().length > 0) ||
        (entry.fromTemplateId &&
          entry.fromTemplateId.trim().length > 0) ||
        (entry.notes && entry.notes.trim().length > 0);

      if (!hasContent) {
        continue;
      }

      let structuredEncoding;
      if (entry.structuredEncodingJson && entry.structuredEncodingJson.trim()) {
        try {
          structuredEncoding = JSON.parse(entry.structuredEncodingJson);
        } catch (error) {
          setNodeState((prev) => ({
            ...prev,
            saving: false,
            error:
              `Relevance scoring ${index + 1}: Invalid structured mapping JSON. ${
                error.message || ""
              }`.trim(),
          }));
          return;
        }
      }

      let version = undefined;
      if (versionValue && versionValue.length > 0) {
        const parsedVersion = Number(versionValue);
        if (Number.isNaN(parsedVersion)) {
          setNodeState((prev) => ({
            ...prev,
            saving: false,
            error: `Relevance scoring ${index + 1}: Version must be a number.`,
          }));
          return;
        }
        version = parsedVersion;
      }

      rateCodingPayload.push({
        id: entry.id || undefined,
        version,
        isActive: entry.isActive === "false" ? false : true,
        structuredEncoding: structuredEncoding ?? undefined,
        naturalLanguageEncoding:
          entry.naturalLanguageEncoding?.trim() || undefined,
        fromTemplateId: entry.fromTemplateId?.trim() || undefined,
        notes: entry.notes?.trim() || undefined,
      });
    }

    if (rateCodingPayload.length > 0) {
      related.rateCoding = rateCodingPayload;
    }

    // Handle node settings entries (many-to-one)
    const nodeSettingsEntries = nodeState.settings || [];
    const nodeSettingsPayload = [];
    for (let index = 0; index < nodeSettingsEntries.length; index += 1) {
      const entry = nodeSettingsEntries[index];
      const hasContent =
        (entry.neuronTemplateId && entry.neuronTemplateId.trim().length > 0) ||
        (entry.nodeSettingType && entry.nodeSettingType.trim().length > 0) ||
        (entry.nodeSettingsName && entry.nodeSettingsName.trim().length > 0) ||
        (entry.explanation && entry.explanation.trim().length > 0) ||
        (entry.description && entry.description.trim().length > 0) ||
        (entry.boundedDataJson && entry.boundedDataJson.trim().length > 0) ||
        (entry.nodeNeuronSettingsJson &&
          entry.nodeNeuronSettingsJson.trim().length > 0) ||
        (entry.dataClassificationId &&
          entry.dataClassificationId.trim().length > 0);

      if (!hasContent) {
        continue;
      }

      let boundedData;
      if (entry.boundedDataJson && entry.boundedDataJson.trim()) {
        try {
          boundedData = JSON.parse(entry.boundedDataJson);
        } catch (error) {
          setNodeState((prev) => ({
            ...prev,
            saving: false,
            error: `Node setting ${index + 1}: Invalid bounded data JSON. ${
              error.message || ""
            }`.trim(),
          }));
          return;
        }
      }

      let nodeNeuronSettings;
      if (entry.nodeNeuronSettingsJson && entry.nodeNeuronSettingsJson.trim()) {
        try {
          nodeNeuronSettings = JSON.parse(entry.nodeNeuronSettingsJson);
        } catch (error) {
          setNodeState((prev) => ({
            ...prev,
            saving: false,
            error: `Node setting ${
              index + 1
            }: Invalid node settings JSON. ${error.message || ""}`.trim(),
          }));
          return;
        }
      }

      nodeSettingsPayload.push({
        id: entry.id || undefined,
        neuronTemplateId: entry.neuronTemplateId?.trim() || undefined,
        type: entry.nodeSettingType?.trim() || undefined,
        name: entry.nodeSettingsName?.trim() || undefined,
        explanation: entry.explanation?.trim() || undefined,
        description: entry.description?.trim() || undefined,
        boundedData: boundedData ?? undefined,
        nodeNeuronSettings: nodeNeuronSettings ?? undefined,
        dataClassificationId: entry.dataClassificationId?.trim() || undefined,
      });
    }

    if (nodeSettingsPayload.length > 0) {
      related.settings = nodeSettingsPayload;
    }

    // Handle remediation entries (many-to-one)
    const remediationEntries = nodeState.remediation || [];
    const remediationPayload = [];
    for (let index = 0; index < remediationEntries.length; index += 1) {
      const entry = remediationEntries[index];
      const hasContent =
        (entry.reason && entry.reason.trim().length > 0) ||
        (entry.autoAttemptNotes && entry.autoAttemptNotes.trim().length > 0) ||
        (entry.resolutionNotes && entry.resolutionNotes.trim().length > 0) ||
        (entry.status && entry.status.trim().length > 0);

      if (!hasContent) {
        continue;
      }

      remediationPayload.push({
        id: entry.id || undefined,
        reason: entry.reason?.trim() || undefined,
        autoAttempted: entry.autoAttempted === "false" ? false : true,
        autoAttemptNotes: entry.autoAttemptNotes?.trim() || undefined,
        status: entry.status?.trim() || undefined,
        resolutionNotes: entry.resolutionNotes?.trim() || undefined,
      });
    }

    if (remediationPayload.length > 0) {
      related.remediation = remediationPayload;
    }

    // Handle effective settings (one-to-one)
    const effectiveSettings = nodeState.effectiveSettings || {};
    if (
      effectiveSettings.resolvedSettingsJson ||
      effectiveSettings.source
    ) {
      let resolvedSettings;
      if (effectiveSettings.resolvedSettingsJson && effectiveSettings.resolvedSettingsJson.trim()) {
        try {
          resolvedSettings = JSON.parse(effectiveSettings.resolvedSettingsJson);
        } catch (error) {
          setNodeState((prev) => ({
            ...prev,
            saving: false,
            error: `Invalid resolved settings JSON. ${error.message || ""}`.trim(),
          }));
          return;
        }
      }
      related.effectiveSettings = {
        resolvedSettings: resolvedSettings ?? undefined,
        source: effectiveSettings.source?.trim() || undefined,
      };
    }

    if (Object.keys(related).length > 0) {
      payload.related = related;
    }

    try {
      const response = await apiService.createNode(payload);
      if (response.success && response.node) {
        setNodeState((prev) => ({
          ...prev,
          saving: false,
          success: "Node created successfully",
          node: response.node,
        }));
        // Invalidate node cache when node is created (graph changed)
        if (graphId) {
          invalidateNodeCache(graphId);
        }
        if (typeof onEntityUpdated === "function") {
          onEntityUpdated();
        }
        // Close modal after a short delay to show success message
        setTimeout(() => {
          onClose();
        }, 1500);
      } else {
        throw new Error(response.message || "Unable to create node.");
      }
    } catch (error) {
      setNodeState((prev) => ({
        ...prev,
        saving: false,
        error: error.message || "Failed to create node.",
      }));
    }
  };

  const handleSaveNode = async (kind) => {
    if (!nodeId) {
      // In add mode, call handleCreateNode instead
      await handleCreateNode();
      return;
    }

    const currentState = nodeState;
    setNodeState((prev) => ({
      ...prev,
      saving: true,
      error: null,
      success: null,
    }));

    // Parse attributes JSON
    let attributes = null;
    try {
      attributes = parseAttributesFromJson(currentState.form.attributesJson);
    } catch (error) {
      setNodeState((prev) => ({
        ...prev,
        saving: false,
        error: error.message || "Invalid JSON in attributes.",
      }));
      return;
    }

    const payload = {
      name: currentState.form.name.trim(),
      type: currentState.form.type.trim(),
      description: currentState.form.description.trim(),
      aliases: Array.isArray(currentState.form.aliases) ? currentState.form.aliases : [],
    };

    // Only include attributes if they were provided
    if (attributes !== null) {
      payload.attributes = attributes;
    }

    // Add retentionPolicyId if provided
    if (
      currentState.form.retentionPolicyId !== undefined &&
      currentState.form.retentionPolicyId !== ""
    ) {
      payload.retentionPolicyId =
        currentState.form.retentionPolicyId.trim() || null;
    }

    // Add nodeKindId if provided
    if (
      currentState.form.nodeKindId !== undefined &&
      currentState.form.nodeKindId !== ""
    ) {
      payload.nodeKindId = currentState.form.nodeKindId.trim() || null;
    }

    // Add related entities if they have values
    const related = {};
    if (
      currentState.form.fusionMode ||
      currentState.form.confidenceTiebreak ||
      currentState.form.fusionParameters
    ) {
      related.fusionPolicy = {
        fusionMode: currentState.form.fusionMode.trim() || undefined,
        confidenceTiebreak:
          currentState.form.confidenceTiebreak.trim() || undefined,
        parameters: currentState.form.fusionParameters
          ? (() => {
              try {
                return JSON.parse(currentState.form.fusionParameters);
              } catch {
                return {};
              }
            })()
          : undefined,
      };
    }

    // Handle rate coding entries (many-to-one)
    const rateCodingEntries = currentState.rateCoding || [];
    const rateCodingPayload = [];
    for (let index = 0; index < rateCodingEntries.length; index += 1) {
      const entry = rateCodingEntries[index];
      const versionValue = entry.version?.toString().trim();
      const hasContent =
        (versionValue && versionValue.length > 0) ||
        (entry.structuredEncodingJson &&
          entry.structuredEncodingJson.trim().length > 0) ||
        (entry.naturalLanguageEncoding &&
          entry.naturalLanguageEncoding.trim().length > 0) ||
        (entry.fromTemplateId &&
          entry.fromTemplateId.trim().length > 0) ||
        (entry.notes && entry.notes.trim().length > 0);

      if (!hasContent) {
        continue;
      }

      let structuredEncoding;
      if (entry.structuredEncodingJson && entry.structuredEncodingJson.trim()) {
        try {
          structuredEncoding = JSON.parse(entry.structuredEncodingJson);
        } catch (error) {
          setNodeState((prev) => ({
            ...prev,
            saving: false,
            error:
              `Relevance scoring ${index + 1}: Invalid structured mapping JSON. ${
                error.message || ""
              }`.trim(),
          }));
          return;
        }
      }

      let version = undefined;
      if (versionValue && versionValue.length > 0) {
        const parsedVersion = Number(versionValue);
        if (Number.isNaN(parsedVersion)) {
          setNodeState((prev) => ({
            ...prev,
            saving: false,
            error: `Relevance scoring ${index + 1}: Version must be a number.`,
          }));
          return;
        }
        version = parsedVersion;
      }

      rateCodingPayload.push({
        id: entry.id || undefined,
        version,
        isActive: entry.isActive === "false" ? false : true,
        structuredEncoding: structuredEncoding ?? undefined,
        naturalLanguageEncoding:
          entry.naturalLanguageEncoding?.trim() || undefined,
        fromTemplateId: entry.fromTemplateId?.trim() || undefined,
        notes: entry.notes?.trim() || undefined,
      });
    }

    if (rateCodingPayload.length > 0) {
      related.rateCoding = rateCodingPayload;
    }

    // Handle node settings entries (many-to-one)
    const nodeSettingsEntries = currentState.settings || [];
    const nodeSettingsPayload = [];
    for (let index = 0; index < nodeSettingsEntries.length; index += 1) {
      const entry = nodeSettingsEntries[index];
      const hasContent =
        (entry.neuronTemplateId && entry.neuronTemplateId.trim().length > 0) ||
        (entry.nodeSettingType && entry.nodeSettingType.trim().length > 0) ||
        (entry.nodeSettingsName && entry.nodeSettingsName.trim().length > 0) ||
        (entry.explanation && entry.explanation.trim().length > 0) ||
        (entry.description && entry.description.trim().length > 0) ||
        (entry.boundedDataJson && entry.boundedDataJson.trim().length > 0) ||
        (entry.nodeNeuronSettingsJson &&
          entry.nodeNeuronSettingsJson.trim().length > 0) ||
        (entry.dataClassificationId &&
          entry.dataClassificationId.trim().length > 0);

      if (!hasContent) {
        continue;
      }

      let boundedData;
      if (entry.boundedDataJson && entry.boundedDataJson.trim()) {
        try {
          boundedData = JSON.parse(entry.boundedDataJson);
        } catch (error) {
          setNodeState((prev) => ({
            ...prev,
            saving: false,
            error: `Node setting ${index + 1}: Invalid bounded data JSON. ${
              error.message || ""
            }`.trim(),
          }));
          return;
        }
      }

      let nodeNeuronSettings;
      if (entry.nodeNeuronSettingsJson && entry.nodeNeuronSettingsJson.trim()) {
        try {
          nodeNeuronSettings = JSON.parse(entry.nodeNeuronSettingsJson);
        } catch (error) {
          setNodeState((prev) => ({
            ...prev,
            saving: false,
            error: `Node setting ${
              index + 1
            }: Invalid node settings JSON. ${error.message || ""}`.trim(),
          }));
          return;
        }
      }

      nodeSettingsPayload.push({
        id: entry.id || undefined,
        neuronTemplateId: entry.neuronTemplateId?.trim() || undefined,
        type: entry.nodeSettingType?.trim() || undefined,
        name: entry.nodeSettingsName?.trim() || undefined,
        explanation: entry.explanation?.trim() || undefined,
        description: entry.description?.trim() || undefined,
        boundedData: boundedData ?? undefined,
        nodeNeuronSettings: nodeNeuronSettings ?? undefined,
        dataClassificationId: entry.dataClassificationId?.trim() || undefined,
      });
    }

    if (nodeSettingsPayload.length > 0) {
      related.settings = nodeSettingsPayload;
    }

    // Handle remediation entries (many-to-one)
    const remediationEntries = currentState.remediation || [];
    const remediationPayload = [];
    for (let index = 0; index < remediationEntries.length; index += 1) {
      const entry = remediationEntries[index];
      const hasContent =
        (entry.reason && entry.reason.trim().length > 0) ||
        (entry.autoAttemptNotes && entry.autoAttemptNotes.trim().length > 0) ||
        (entry.resolutionNotes && entry.resolutionNotes.trim().length > 0) ||
        (entry.status && entry.status.trim().length > 0);

      if (!hasContent) {
        continue;
      }

      remediationPayload.push({
        id: entry.id || undefined,
        reason: entry.reason?.trim() || undefined,
        autoAttempted: entry.autoAttempted === "false" ? false : true,
        autoAttemptNotes: entry.autoAttemptNotes?.trim() || undefined,
        status: entry.status?.trim() || undefined,
        resolutionNotes: entry.resolutionNotes?.trim() || undefined,
      });
    }

    if (remediationPayload.length > 0) {
      related.remediation = remediationPayload;
    }

    // Handle effective settings (one-to-one)
    const effectiveSettings = currentState.effectiveSettings || {};
    if (
      effectiveSettings.resolvedSettingsJson ||
      effectiveSettings.source
    ) {
      let resolvedSettings;
      if (effectiveSettings.resolvedSettingsJson && effectiveSettings.resolvedSettingsJson.trim()) {
        try {
          resolvedSettings = JSON.parse(effectiveSettings.resolvedSettingsJson);
        } catch (error) {
          setNodeState((prev) => ({
            ...prev,
            saving: false,
            error: `Invalid resolved settings JSON. ${error.message || ""}`.trim(),
          }));
          return;
        }
      }
      related.effectiveSettings = {
        resolvedSettings: resolvedSettings ?? undefined,
        source: effectiveSettings.source?.trim() || undefined,
      };
    }

    if (Object.keys(related).length > 0) {
      payload.related = related;
    }

    try {
      const response = await apiService.updateNode(nodeId, payload);
      if (response.success && response.node) {
        const refreshedRateCoding = response.node?.related?.rateCoding
          ? mapRateCodingFromRelated(response.node.related.rateCoding)
          : currentState.rateCoding;
        const refreshedSettings = response.node?.related?.settings
          ? mapNodeSettingsFromRelated(response.node.related.settings)
          : currentState.settings;
        const refreshedRemediation = response.node?.related?.remediation
          ? mapRemediationFromRelated(response.node.related.remediation)
          : currentState.remediation;
        const refreshedEffectiveSettings = response.node?.related?.effectiveSettings
          ? {
              resolvedSettingsJson: response.node.related.effectiveSettings.resolvedSettings
                ? JSON.stringify(response.node.related.effectiveSettings.resolvedSettings, null, 2)
                : "",
              resolvedAt: response.node.related.effectiveSettings.resolvedAt || "",
              source: response.node.related.effectiveSettings.source || "",
            }
          : currentState.effectiveSettings;
        setNodeState((prev) => ({
          ...prev,
          saving: false,
          success: "Changes saved",
          node: response.node,
          rateCoding: refreshedRateCoding,
          settings: refreshedSettings,
          remediation: refreshedRemediation,
          effectiveSettings: refreshedEffectiveSettings,
        }));
        // Invalidate node cache when node is updated (graph changed)
        if (graphId) {
          invalidateNodeCache(graphId);
          // Trigger effective settings rebuild + SNN cache invalidation
          try {
            await graphBuilderClient.post('/api/runtime/on-graph-save', { graph_id: graphId });
          } catch (e) {
            console.warn('[NodeEntityModal] on-graph-save trigger failed:', e);
          }
        }
        if (typeof onEntityUpdated === "function") {
          onEntityUpdated();
        }
      } else {
        throw new Error(response.message || "Unable to update node.");
      }
    } catch (error) {
      setNodeState((prev) => ({
        ...prev,
        saving: false,
        error: error.message || "Failed to update node.",
      }));
    }
  };

  const handleRateCodingChange = (kind, index, field, value) => {
    setNodeState((prev) => {
      const currentEntries = prev.rateCoding || [];
      const nextEntries = currentEntries.map((entry, entryIndex) =>
        entryIndex === index ? { ...entry, [field]: value } : entry
      );
      return {
        ...prev,
        rateCoding: nextEntries,
      };
    });
  };

  const handleAddRateCoding = (kind) => {
    setNodeState((prev) => ({
      ...prev,
      rateCoding: [...(prev.rateCoding || []), createRateCodingEntry()],
    }));
  };

  const handleRemoveRateCoding = (kind, index) => {
    setNodeState((prev) => {
      const currentEntries = prev.rateCoding || [];
      return {
        ...prev,
        rateCoding: currentEntries.filter((_, i) => i !== index),
      };
    });
  };

  const handleNodeSettingsChange = (kind, index, field, value) => {
    setNodeState((prev) => {
      const currentEntries = prev.settings || [];
      const nextEntries = currentEntries.map((entry, entryIndex) =>
        entryIndex === index ? { ...entry, [field]: value } : entry
      );
      return {
        ...prev,
        settings: nextEntries,
      };
    });
  };

  const handleAddNodeSetting = (kind) => {
    setNodeState((prev) => ({
      ...prev,
      settings: [...(prev.settings || []), createNodeSettingEntry()],
    }));
  };

  const handleRemoveNodeSetting = (kind, index) => {
    setNodeState((prev) => {
      const currentEntries = prev.settings || [];
      return {
        ...prev,
        settings: currentEntries.filter((_, i) => i !== index),
      };
    });
  };

  const handleEffectiveSettingsChange = (kind, field, value) => {
    setNodeState((prev) => ({
      ...prev,
      effectiveSettings: {
        ...(prev.effectiveSettings || {
          resolvedSettingsJson: "",
          resolvedAt: "",
          source: "",
        }),
        [field]: value,
      },
    }));
  };

  const handleRemediationChange = (kind, index, field, value) => {
    setNodeState((prev) => {
      const currentEntries = prev.remediation || [];
      const nextEntries = currentEntries.map((entry, entryIndex) =>
        entryIndex === index ? { ...entry, [field]: value } : entry
      );
      return {
        ...prev,
        remediation: nextEntries,
      };
    });
  };

  const handleAddRemediation = (kind) => {
    setNodeState((prev) => ({
      ...prev,
      remediation: [...(prev.remediation || []), createRemediationEntry()],
    }));
  };

  const handleRemoveRemediation = (kind, index) => {
    setNodeState((prev) => {
      const currentEntries = prev.remediation || [];
      return {
        ...prev,
        remediation: currentEntries.filter((_, i) => i !== index),
      };
    });
  };

  const renderNodeForm = () => {
    if (nodeState.loading) {
      return (
        <p className="synapse-entity-modal__status">Loading node details…</p>
      );
    }

    return (
      <NodeForm
        kind={KIND_NODE}
        state={nodeState}
        handleNodeInputChange={handleNodeInputChange}
        handleSaveNode={handleSaveNode}
        handleRateCodingChange={handleRateCodingChange}
        handleAddRateCoding={handleAddRateCoding}
        handleRemoveRateCoding={handleRemoveRateCoding}
        handleNodeSettingsChange={handleNodeSettingsChange}
        handleAddNodeSetting={handleAddNodeSetting}
        handleRemoveNodeSetting={handleRemoveNodeSetting}
        handleEffectiveSettingsChange={handleEffectiveSettingsChange}
        graphId={graphId}
        nodeId={nodeState.nodeId}
      />
    );
  };

  // Allow modal to open even without node (for add mode)
  if (!isOpen) {
    return null;
  }

  const isAddMode = !node;

  return (
    <div className="synapse-modal-overlay" onClick={onClose}>
      <div
        className="synapse-entity-modal"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="synapse-entity-modal__header">
          <div>
            <p className="synapse-entity-modal__eyebrow">Node</p>
            <h2>{isAddMode ? "Create node" : "Edit node"}</h2>
            {!isAddMode && node && (
              <p className="synapse-entity-modal__subhead">
                {node.name || node.nodeName || "—"}
              </p>
            )}
          </div>
          <button
            type="button"
            className="synapse-modal-close"
            onClick={onClose}
            aria-label="Close"
          >
            ×
          </button>
        </div>
        <div className="synapse-entity-modal__body">
          {renderNodeForm()}

          {/* MLCF Attributes (from execution trace prop or node API) */}
          {effectiveMlcf && (
            <div style={{ marginTop: 16, padding: '12px 16px', background: '#f8f9fb', borderRadius: 8, border: '1px solid #e5e7eb' }}>
              <h4 style={{ margin: '0 0 8px', fontSize: '0.95em', fontWeight: 600, color: '#374151' }}>
                MLCF Attributes
              </h4>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px 16px', fontSize: '0.85em' }}>
                {effectiveMlcf.identity_signature && (
                  <>
                    <div>
                      <strong style={{ color: '#6b7280' }}>Identity Entity:</strong>{' '}
                      {effectiveMlcf.identity_signature.entity || '-'}
                    </div>
                    <div>
                      <strong style={{ color: '#6b7280' }}>Qualifiers:</strong>{' '}
                      {(effectiveMlcf.identity_signature.qualifiers || []).join(', ') || '-'}
                    </div>
                    {effectiveMlcf.identity_signature.role && (
                      <div>
                        <strong style={{ color: '#6b7280' }}>Role:</strong>{' '}
                        {effectiveMlcf.identity_signature.role}
                      </div>
                    )}
                  </>
                )}
                {effectiveMlcf.scope && (
                  <>
                    <div>
                      <strong style={{ color: '#6b7280' }}>Scope Domain:</strong>{' '}
                      {effectiveMlcf.scope.domain || '-'}
                    </div>
                    <div>
                      <strong style={{ color: '#6b7280' }}>Scope Context:</strong>{' '}
                      {effectiveMlcf.scope.context || '-'}
                    </div>
                    {(effectiveMlcf.scope.applicability || []).length > 0 && (
                      <div style={{ gridColumn: '1 / -1' }}>
                        <strong style={{ color: '#6b7280' }}>Applicability:</strong>{' '}
                        {effectiveMlcf.scope.applicability.join(', ')}
                      </div>
                    )}
                    {(effectiveMlcf.scope.constraints || []).length > 0 && (
                      <div style={{ gridColumn: '1 / -1' }}>
                        <strong style={{ color: '#6b7280' }}>Constraints:</strong>{' '}
                        {effectiveMlcf.scope.constraints.join(', ')}
                      </div>
                    )}
                  </>
                )}
                <div>
                  <strong style={{ color: '#6b7280' }}>Context Layer:</strong>{' '}
                  <span style={{
                    display: 'inline-block', padding: '1px 6px', borderRadius: 4,
                    background: '#e0e7ff', color: '#4338ca', fontSize: '0.9em', fontWeight: 500
                  }}>
                    {effectiveMlcf.context_layer || '-'}
                  </span>
                </div>
                <div>
                  <strong style={{ color: '#6b7280' }}>Structural Role:</strong>{' '}
                  <span style={{
                    display: 'inline-block', padding: '1px 6px', borderRadius: 4,
                    background: '#fef3c7', color: '#92400e', fontSize: '0.9em', fontWeight: 500
                  }}>
                    {effectiveMlcf.structural_role || '-'}
                  </span>
                </div>
                {effectiveMlcf.abstraction_level && (
                  <div>
                    <strong style={{ color: '#6b7280' }}>Abstraction Level:</strong>{' '}
                    {effectiveMlcf.abstraction_level}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default NodeEntityModal;

